import t from '@jetshop/intl';
import React, { useMemo } from 'react';
import { css, cx } from 'react-emotion';
import { Link } from 'react-router-dom';
import FilterablePage from '../Layout/FilterablePage/FilterablePage';
import { theme } from '../Theme';
import useShopId from '../../utils/useShopId';

const brandsStyling = css`
  .items-container {
    display: flex;
    flex-wrap: wrap;
    margin: 2.375rem -1.875rem 0;

    .column {
      width: calc(33.3333% - 3.75rem);
      margin: 0 1.875rem 5rem;
      display: flex;

      .letter {
        width: 50px;
        font-weight: 700;
        color: ${theme.colors.green};
        font-size: 1.5rem;
        padding: 0.6725rem 0;
        text-transform: uppercase;
      }

      .letter.eztrad {
        color: ${theme.colors.almostBlack};
      }

      .brands {
        flex: 1;

        ul li a {
          padding: 1rem 0;
          color: ${theme.colors.black};
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
          display: block;

          :hover span {
            border-bottom: 1px solid ${theme.colors.black};
          }
        }
      }
    }
  }

  ${theme.below.md} {
    .items-container {
      margin-left: -1rem;
      margin-left: -1rem;
      .column {
        margin: 0 1rem 2.5rem;
        width: calc(50% - 3rem);
      }
    }
  }

  ${theme.below.sm} {
    .items-container {
      margin: 2.1875rem 0 0;

      .column {
        width: 100%;
        flex-direction: column;
        margin: 0 0 2.5rem;

        .letter {
          border-bottom: 1px solid;
          padding: 0 0 0.375rem;
          border-color: ${theme.colors.mediumGrey};
          width: 100%;
        }

        .brands ul li a {
          border-bottom: 1px solid;
          border-color: ${theme.colors.mediumGrey};
        }
      }
    }
  }
`;

function abcSort(list, key) {
  return list.sort(function(a, b) {
    if (a[key] < b[key]) {
      return -1;
    }
    if (a[key] > b[key]) {
      return 1;
    }
    return 0;
  });
}

const ItemComponent = ({ items, letter }) => {
  const isEztrad = useShopId() === 'eztrad';
  return (
    <div className="column" key={letter}>
      <div className={cx('letter', isEztrad && 'eztrad')}>{letter}</div>
      <div className="brands">
        <ul>
          {items.map(({ name, path }) => (
            <li key={name}>
              <Link to={path}>
                <span>{name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const WindowedCategory = ({
  category,
  cacheHit,
  result,
  className,
  ...props
}) => {
  const { brandList, buttonList } = useMemo(() => {
    let brandList = [];
    let buttonList = [{ key: '', buttonText: t('See all') }];

    category.subcategories.forEach(({ name, primaryRoute: { path } }) => {
      let firstChar = name.toLowerCase().charAt(0);

      // If the first character is a number, it goes into '0-9'
      if (!isNaN(firstChar)) firstChar = '0-9';

      if (!buttonList.find(brand => brand.key === firstChar)) {
        buttonList.push({ key: firstChar });
        brandList.push({ key: firstChar, items: [{ name, path }] });
      } else {
        brandList
          .find(brand => brand.key === firstChar)
          .items.push({ name, path });
      }
    });

    buttonList = abcSort(buttonList, 'key');
    brandList = abcSort(brandList, 'key');
    for (let x = 0; x < brandList.length; x++) {
      brandList[x].items = abcSort(brandList[x].items, 'name');
      brandList[x].Item = () => (
        <ItemComponent items={brandList[x].items} letter={brandList[x].key} />
      );
    }

    return { brandList, buttonList };
  }, [category.subcategories]);

  return (
    <FilterablePage
      itemList={brandList}
      buttonList={buttonList}
      pageHeaderProps={{
        title: category.name,
        content: category.content,
        breadcrumbText: category.breadcrumbText,
        images: category.images,
        parents: result.data?.route?.parents || null
      }}
      cacheHit={cacheHit}
      className={cx(className, brandsStyling)}
      {...props}
    />
  );
};

export default WindowedCategory;
